/**
 * "Tem versão nova no ar?" — a pergunta que só o servidor responde.
 *
 * O painel é uma SPA: quem deixa a aba aberta continua rodando o bundle que baixou quando
 * abriu, e um deploy não muda isso. Dois problemas saem daí, e o segundo é o grave:
 *
 *  1. a pessoa opera numa versão antiga sem saber;
 *  2. ao navegar para uma tela que ainda não tinha sido carregada, o navegador vai buscar um
 *     pedaço de código (`/assets/…-a1b2c3.js`) que **já não existe** — o deploy trocou os
 *     nomes. A tela simplesmente não abre, e o erro não diz "atualize a página".
 *
 * O sinal é `version.json`, publicado pelo build (ver `vite.config.ts`). A versão é o hash dos
 * nomes dos arquivos gerados: build que não muda nada mantém a mesma versão, então o aviso não
 * aparece à toa. Aviso que aparece à toa é aviso que se aprende a ignorar.
 */

/** Onde o build publica a identidade. Servido com `no-store` (ver o nginx). */
export const VERSION_URL = '/version.json';

export type LeituraDeVersao =
  | { estado: 'lida'; build: string }
  /**
   * Não deu para saber. **Isto não é "não mudou"** (invariante 12): é o que acontece offline,
   * com o servidor reiniciando no meio do deploy, ou em desenvolvimento, onde o arquivo nem
   * existe. Quem lê isto não pode concluir nada — e muito menos avisar o usuário.
   */
  | { estado: 'indisponivel' };

export async function lerVersao(
  fetchImpl: typeof fetch = fetch,
  url: string = VERSION_URL,
): Promise<LeituraDeVersao> {
  try {
    // `no-store` no fetch E no nginx: um cache intermediário que guardasse este arquivo
    // faria o painel nunca descobrir que mudou — exatamente o bug que ele existe para evitar.
    const resposta = await fetchImpl(url, { cache: 'no-store', credentials: 'omit' });
    if (!resposta.ok) return { estado: 'indisponivel' };

    const corpo: unknown = await resposta.json();
    const build =
      typeof corpo === 'object' && corpo !== null && 'build' in corpo
        ? (corpo as { build: unknown }).build
        : null;

    return typeof build === 'string' && build.length > 0
      ? { estado: 'lida', build }
      : { estado: 'indisponivel' };
  } catch {
    return { estado: 'indisponivel' };
  }
}

/**
 * Comparação de uma linha, isolada porque é onde o erro sutil mora.
 *
 * `indisponivel` **nunca** vira "mudou": offline avisaria atualização a cada queda de rede, e
 * recarregar offline deixa a pessoa numa página em branco.
 */
export function mudou(atual: string | null, leitura: LeituraDeVersao): boolean {
  if (leitura.estado !== 'lida') return false;
  if (atual === null) return false;
  return leitura.build !== atual;
}
